import { FC } from 'react'
import { Tabs } from 'antd'
import { useTranslation } from 'react-i18next'
import shallow from 'zustand/shallow'
import Cascader from 'antd/lib/cascader'
import { useAuth } from '../../context/auth-context'
import { PerTabBarContent } from '../../components/custom_components/TabBarContent'
import { CompanySettingsPermissions } from './permissions'
import { getCompanySettingsPermissionInfo } from '../../shared/permissions/permission-helper'
import { useCompanySettingsStore } from './CompanySettingsStore'
import { CompanySettingsTabs } from './types'
import { Country } from '../../types'
import './index.less'
import useIsFeatureEnabled from '../../graphql/featureToggle/useIsFeatureEnabled'
import { FeatureNameEnum } from '../../graphql/featureToggle/FeatureNameEnum'
import { searchOptionsFilter } from '../../components/custom_components/Cascader/util'
import { useCascaderSearchOnOpen } from '../../components/custom_components/Cascader/useCascaderSearchOnOpen'
import { getCompanyCascaderSearchPlaceholder } from './utils/getCompanyCascaderSearchPlaceholder'

const { TabPane } = Tabs

export const CompanySettingsHeader: FC = () => {
  const { t } = useTranslation()
  const { user } = useAuth()

  const { isFeatureEnabled: rankingPhaseTwoEnabled } = useIsFeatureEnabled(FeatureNameEnum.TALENTS_SEASON)

  const [activeTab, setActiveTab, companiesForSelection, companySelectionCurrentValue, setSelected] = useCompanySettingsStore(
    (state) => [state.activeTab, state.setActiveTab, state.companiesForSelection, state.companySelectionCurrentValue, state.setSelected],
    shallow,
  )

  const companySettingsPermissionInfo = getCompanySettingsPermissionInfo(user.roles ? user.roles : [])
  const { cascaderRef, cascaderClassName, searchPlaceholder, onPopupVisibleChange } = useCascaderSearchOnOpen(
    getCompanyCascaderSearchPlaceholder(t, companiesForSelection),
  )

  const onDropDownChanged = (values: string[]) => {
    values.length > 1 ? setSelected({ companyId: values[0], country: values[1] as Country }) : setSelected({ companyId: values[0] })
  }

  return (
    <PerTabBarContent
      tabs={
        <Tabs tabBarStyle={{ maxWidth: 750 }} activeKey={activeTab} onChange={(key) => setActiveTab(key)}>
          {companySettingsPermissionInfo[CompanySettingsPermissions.appraisalsTab] && (
            <TabPane tab={t('companySettingsView.appraisalTab.heading')} key={CompanySettingsTabs.appraisal} />
          )}
          {companySettingsPermissionInfo[CompanySettingsPermissions.companyMeetingRequests] && (
            <TabPane tab={t('companySettingsView.requestedMeetingsTab.heading')} key={CompanySettingsTabs.requests} />
          )}
          {companySettingsPermissionInfo[CompanySettingsPermissions.reviewerTab] && (
            <TabPane tab={t('companySettingsView.reviewerTab.heading')} key={CompanySettingsTabs.reviewer} />
          )}
          {companySettingsPermissionInfo[CompanySettingsPermissions.employeesTab] && (
            <TabPane tab={t('companySettingsView.employeesTab.heading')} key={CompanySettingsTabs.employees} />
          )}
          {companySettingsPermissionInfo[CompanySettingsPermissions.cultivatingTalentsTab] && !!rankingPhaseTwoEnabled && (
            <TabPane tab={t('companySettingsView.rankingPhaseTwoTab.heading')} key={CompanySettingsTabs.cultivatingTalents} />
          )}
          {companySettingsPermissionInfo[CompanySettingsPermissions.targetTemplatesTab] && (
            <TabPane tab={t('companySettingsView.targetTemplatesTab.heading')} key={CompanySettingsTabs.targetTemplate} />
          )}
          {companySettingsPermissionInfo[CompanySettingsPermissions.targetReportsTab] && (
            <TabPane tab={t('companySettingsView.targetReportsTab.heading')} key={CompanySettingsTabs.targetReports} />
          )}
        </Tabs>
      }
      actions={
        <Cascader
          ref={cascaderRef}
          className={cascaderClassName}
          options={companiesForSelection}
          onChange={onDropDownChanged}
          placeholder={searchPlaceholder}
          allowClear={false}
          value={companySelectionCurrentValue}
          style={{ width: '100%', marginLeft: '3.5px' }}
          showSearch={{ filter: searchOptionsFilter }}
          onPopupVisibleChange={onPopupVisibleChange}
        />
      }
    />
  )
}
